import { action, Action, computed, Computed, thunk, Thunk } from 'easy-peasy';
import { ServiceCheck, DeleteServiceResponse, IssueChatPayload, Message, SaveChatUserPayload, SaveChatUserResponse, SearchUser, Service, ServiceDetails, APIResponse, ServiceCompletePayload, ServiceClosePayload, SearchPartResponse, SavePartResponse, SavePartPayload, Part, ServiceSavePayload, Material, MiscMaterial, SaveMaterialPayload, SaveMaterialResponse } from '~/types';
import { StoreModel } from "~/store";
import { API } from "~/app/routes";
import { CommonModel, getCommonModel } from "~/store/commonStore";
import axios from "axios";
import { REQUIRED_INTERVAL_TIMEUNIT, ServiceStatuses } from '~/app/data/statuses/services';
import { serviceRepository } from '~/data/repositories';

let saveServiceRequest: Promise<APIResponse> | null = null;

export interface ServiceModel extends CommonModel<Service> {
    filteredServices: Computed<ServiceModel, Service[], StoreModel>
    hasSimplifiedWorkorder: Computed<ServiceModel, boolean, StoreModel>
    hasNextServiceSection: Computed<ServiceModel, boolean, StoreModel>

    showCompleted: boolean
    setShowCompleted: Action<ServiceModel, boolean>

    isWorkOrderFormActive: boolean
    setIsWorkOrderFormActive: Action<ServiceModel, boolean>

    data: ServiceDetails
    fetchService: Thunk<ServiceModel, ServiceDetails['id'], any, StoreModel, Promise<void>>
    fetchedService: Action<ServiceModel, ServiceDetails>
    cancelService: Thunk<ServiceModel, ServiceDetails['id'], any, StoreModel, Promise<DeleteServiceResponse>>
    addChatMessage: Action<ServiceModel, Message>
    addChatUser: Thunk<ServiceModel, IssueChatPayload, any, StoreModel, Promise<SaveChatUserResponse>>

    fetchFavoriteMachinesService: Thunk<ServiceModel, ServiceDetails['id'], any, StoreModel, Promise<void>>

    serviceChatUserUpdating: boolean
    setServiceChatUserUpdating: Action<ServiceModel, boolean>
    tempChatUsers: SearchUser[]
    setTempChatUsers: Action<ServiceModel, SearchUser[]>
    setChatUsers: Thunk<ServiceModel, SearchUser[]>;
    fetchChatUsers: Thunk<ServiceModel, ServiceDetails['id'], any, StoreModel, Promise<void>>
    saveChatUser: Thunk<ServiceModel, SaveChatUserPayload, any, StoreModel, Promise<SaveChatUserResponse>>

    serviceChecklistUpdating: boolean
    setServiceChecklistUpdating: Action<ServiceModel, boolean>    
    setServiceChecklist: Thunk<ServiceModel, ServiceCheck[]>;
    fetchServiceChecklist: Thunk<ServiceModel, { serviceId: ServiceDetails['id'], serviceIntervalId: ServiceDetails['serviceIntervalId'] }, any, StoreModel, Promise<void>>

    closeService: Thunk<ServiceModel, ServiceClosePayload, any, StoreModel, Promise<APIResponse>>
    completeService: Thunk<ServiceModel, ServiceCompletePayload, any, StoreModel, Promise<APIResponse>>
    saveService: Thunk<ServiceModel, ServiceSavePayload, any, StoreModel, Promise<APIResponse>>

    partUpdating: boolean
    setPartUpdating: Action<ServiceModel, boolean>
    tempParts: Part[]
    setTempParts: Action<ServiceModel, Part[]>
    setParts: Thunk<ServiceModel, Part[]>;
    fetchParts: Thunk<ServiceModel, ServiceDetails['id'], any, StoreModel, Promise<void>>
    searchParts: Part[]
    searchPartsTotal: number
    setSearchedParts: Action<ServiceModel, SearchPartResponse>
    fetchSearchParts: Thunk<ServiceModel, { searchText: string, offset: number }, any, StoreModel, Promise<void>>
    savePart: Thunk<ServiceModel, SavePartPayload, any, StoreModel, Promise<SavePartResponse>>

    materialUpdating: boolean
    setMaterialUpdating: Action<ServiceModel, boolean>
    tempMaterials: Material[]
    setTempMaterials: Action<ServiceModel, Material[]>
    setMaterials: Thunk<ServiceModel, Material[]>;
    fetchMaterials: Thunk<ServiceModel, ServiceDetails['id'], any, StoreModel, Promise<void>>
    miscMaterials: MiscMaterial[]
    setMiscMaterials: Action<ServiceModel, MiscMaterial[]>
    fetchMiscMaterials: Thunk<ServiceModel, number, any, StoreModel, Promise<void>>
    saveMaterials: Thunk<ServiceModel, SaveMaterialPayload, any, StoreModel, Promise<SaveMaterialResponse>>

    isReadOnlyChat: Computed<ServiceModel, boolean, StoreModel>
}

export const serviceModel: ServiceModel = {
    ...getCommonModel<Service>(API.service),

    showCompleted: false,
    setShowCompleted: action((state, payload) => {
        state.showCompleted = payload;
    }),

    isWorkOrderFormActive: false,
    setIsWorkOrderFormActive: action((state, payload) => {
        state.isWorkOrderFormActive = payload;
    }),

    filteredServices: computed(
        [
            state => state.showCompleted,
            (state, storeState) => storeState.machine.data.services
        ],
        (status, services) => services.filter(service =>
            status
            ? (service.status === ServiceStatuses.COMPLETED || service.status === ServiceStatuses.PARTIALLLYCOMPLETED || service.status === ServiceStatuses.CANCELLED)
            : !(service.status === ServiceStatuses.COMPLETED || service.status === ServiceStatuses.PARTIALLLYCOMPLETED || service.status === ServiceStatuses.CANCELLED)
        )
    ),

    hasSimplifiedWorkorder: computed(
        [
            (state, storeState) => storeState.user.data.is_active,
            (state, storeState) => storeState.user.data.departments,
            state => state.data.departmentId,
            state => state.data.hasSimplifiedWorkorderEnabled
        ],
        (is_active, departments, departmentId, hasSimplifiedWorkorderEnabled) => is_active && (departments ?? []).includes(Number(departmentId ?? '-1')) && !!hasSimplifiedWorkorderEnabled
    ),

    hasNextServiceSection: computed(
        [
            state => state.data.serviceProgramType,
            state => state.data.intervalType,
            state => state.data.timeUnit,
            state => state.data.primaryMeterUnit,
            state => state.data.scheduleAt
        ],
        (serviceProgramType, intervalType, timeUnit, primaryMeterUnit, scheduleAt)  => serviceProgramType !== undefined && serviceProgramType !== 0 && intervalType !== 1 && timeUnit !== REQUIRED_INTERVAL_TIMEUNIT && (primaryMeterUnit !== null || scheduleAt !== null)
    ),
    
    data: {
        id: '',
        unitText: '',
        serviceOccurrenceText: '',
        intervalTypeText: '',
        status: ServiceStatuses.PREVIOUS,
        machineId: '',
        machineIntern: '',
        machineName: '',
        statusDate: '',
        departmentId: '',
        departmentName: null,
        departmentPrefix: '',
        workOrderNo: '',
        workDetails: [],
        chat: [],
        chatUsers: [],
        checklist: [],
        createdAt: '',
        updatedAt: '',
        serviceCreatedBy: null,
        serviceUpdatedBy: null,
        projectId: null,
        registrationDate: '',
        dueHours: '',
        serviceStatus: undefined,
        serviceProgramName: '',
        dateDiff: 0,
        parts: [],
        materials: [],
        serviceIntervalId: '',
        workOrderId: null,
        scheduleAt: null,
        primaryMeterUnit: null,
        intervalType: null,
        serviceProgramType: null,
        timeUnit: null,
        operatingTime: 0,
        secondaryMeterUnit: null,
        alertScheduleDays: 0,
        alertServiceOccurrence: 0
    },

    fetchService: thunk(async (actions, payload, { getStoreActions }) => {
        const data = await serviceRepository.getService(payload);
        if (data) {
            actions.fetchedService(data);
            getStoreActions().serviceCheck.loadChecksData({ checks: data.checklist });
        }
    }),
    fetchedService: action((state, payload) => {
        state.data = payload;
    }),
    fetchFavoriteMachinesService: thunk(async (_actions, payload) => {
        await serviceRepository.fetchAndCacheFavoriteServices(String(payload));
    }),

    cancelService:  thunk((actions, payload) => {
        actions.setFetching(true);
        return axios.post<null, DeleteServiceResponse>(`${API.service}/cancel`, {id: payload}).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setFetching(false);
        })
    }),

    serviceChatUserUpdating: false,
    setServiceChatUserUpdating: action((state, payload) => {
        state.serviceChatUserUpdating = payload;
    }),
    tempChatUsers: [],
    setTempChatUsers: action((state, payload) => {
      state.tempChatUsers = payload;
    }),
    setChatUsers: thunk(async (actions, payload, { getState }) => {
        const state = getState();
        state.data.chatUsers = payload;
        state.tempChatUsers = payload;
        await serviceRepository.writeThroughCache(state.data);
    }),
    fetchChatUsers: thunk((actions, payload) => {
        if (String(payload ?? '').trim() === '') {
            return Promise.resolve();
        }
        return axios.post<null, SearchUser[]>(`${API.serviceUsers}/fetch/${payload}`)
            .then(
                data => {
                    actions.setChatUsers(data);
                })
    }),
    saveChatUser: thunk((actions, payload) => {
        actions.setServiceChatUserUpdating(true);
        return axios.post<null, SaveChatUserResponse>(`${API.serviceUsers}/save`, payload).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setServiceChatUserUpdating(false);
        })
    }),
    addChatUser: thunk((actions, payload) => {
        return axios.post<null, SaveChatUserResponse>(`${API.serviceUsers}/add`, payload).then(data => { return data; })
    }),
    addChatMessage: action((state, payload) => {
        Number(state.data.id) === Number(payload.issueId) && state.data.chat.push(payload);
    }),

    serviceChecklistUpdating: false,
    setServiceChecklistUpdating: action((state, payload) => {
        state.serviceChecklistUpdating = payload;
    }),
    setServiceChecklist: thunk(async (actions, payload, { getState }) => {
        const state = getState();
        state.data.checklist = payload;
        await serviceRepository.writeThroughCache(state.data);
    }),
    fetchServiceChecklist: thunk(async (actions, payload, { getState }) => {
        const checks = await serviceRepository.getChecklist(payload, getState().data);
        await actions.setServiceChecklist(checks);
    }),

    closeService: thunk((actions, payload) => {
        actions.setFetching(true);
        return axios.post<null, APIResponse>(`${API.service}/close`, payload).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setFetching(false);
        })
    }),
    completeService: thunk((actions, payload) => {
        actions.setFetching(true);
        return axios.post<null, APIResponse>(`${API.service}/complete`, payload).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setFetching(false);
        })
    }),
    saveService: thunk((actions, payload, { getStoreActions }) => {
        if (saveServiceRequest) {
            return saveServiceRequest;
        }
        actions.setFetching(true);
        getStoreActions().serviceCheck.setFetching(true);
        saveServiceRequest = axios.post<null, APIResponse>(`${API.service}/save`, payload).then(
            data => {
                return data;
            }
        ).finally(() => {
            saveServiceRequest = null;
            actions.setFetching(false);
            getStoreActions().serviceCheck.setFetching(false);
        });
        return saveServiceRequest;
    }),

    partUpdating: false,
    setPartUpdating: action((state, payload) => {
        state.partUpdating = payload;
    }),
    tempParts: [],
    setTempParts: action((state, payload) => {
        state.tempParts = payload;
    }),
    setParts: thunk(async (actions, payload, { getState }) => {
        const state = getState();
        state.data.parts = payload;
        state.tempParts = payload;
        await serviceRepository.writeThroughCache(state.data);
    }),
    fetchParts: thunk((actions, payload) => {
        return axios.post<null, Part[]>(`${API.serviceParts}/fetch/${payload}`)
            .then(
                data => {
                    actions.setParts(data);
                })
    }),
    searchParts: [],
    searchPartsTotal: 0,
    setSearchedParts: action((state, payload) => {
        state.searchParts = payload.parts;
        state.searchPartsTotal = payload.total;
    }),
    fetchSearchParts: thunk((actions, payload) => {
        actions.setPartUpdating(true);
        return axios.post<null, SearchPartResponse>(`${API.serviceParts}/search`, payload).then(
            data => {
                actions.setSearchedParts(data);
            }
        ).finally(() => {
            actions.setPartUpdating(false);
        })
    }),
    savePart: thunk((actions, payload) => {
        actions.setPartUpdating(true);
        return axios.post<null, SavePartResponse>(`${API.serviceParts}/save`, payload).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setPartUpdating(false);
        })
    }),

    materialUpdating: false,
    setMaterialUpdating: action((state, payload) => {
        state.materialUpdating = payload;
    }),
    tempMaterials: [],
    setTempMaterials: action((state, payload) => {
        state.tempMaterials = payload;
    }),
    setMaterials: thunk(async (actions, payload, { getState }) => {
        const state = getState();
        state.data.materials = payload;
        state.tempMaterials = payload;
        await serviceRepository.writeThroughCache(state.data);
    }),
    fetchMaterials: thunk((actions, payload) => {
        return axios.get<null, Material[]>(`${API.serviceMaterials}/fetch/${payload}`)
            .then(data => {
                actions.setMaterials(data);
            });
    }),
    miscMaterials: [],
    setMiscMaterials: action((state, payload) => {
        state.miscMaterials = payload;
    }),
    fetchMiscMaterials: thunk((actions, payload) => {
        actions.setMaterialUpdating(true);
        return axios.get<null, MiscMaterial[]>(`${API.serviceMaterials}/misc/${payload}`)
            .then(data => {
                actions.setMiscMaterials((data ?? []).filter(m => Number(m.id) !== 0));
            })
            .finally(() => {
                actions.setMaterialUpdating(false);
            });
    }),
    saveMaterials: thunk((actions, payload) => {
        actions.setMaterialUpdating(true);
        return axios.post<null, SaveMaterialResponse>(`${API.serviceMaterials}/save`, payload).then(
            data => data
        ).finally(() => {
            actions.setMaterialUpdating(false);
        });
    }),

    isReadOnlyChat: computed(
        [state => state.data.status],
        (status) => false
    ),
};
